import React, { useState } from 'react';
import {
  RocketLaunchIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  EyeIcon,
  CreditCardIcon,
  XCircleIcon,
  StarIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  EnvelopeIcon,
  UserGroupIcon,
  UserIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import Tutorial from './Tutorial';

const Help = () => {
  const [activeTab, setActiveTab] = useState('parents');
  const [openSections, setOpenSections] = useState({});
  // ?tour=1 opens the interactive tutorial; ?tour=parent / ?tour=sitter skips
  // role selection too (usable in role-specific onboarding emails)
  const tourParam = new URLSearchParams(window.location.search).get('tour');
  const [showTutorial, setShowTutorial] = useState(() => !!tourParam);

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const FAQItem = ({ question, answer, id }) => {
    const isOpen = openSections[id];
    return (
      <div className="border-b border-gray-100 last:border-b-0">
        <button
          onClick={() => toggleSection(id)}
          className="w-full py-5 px-6 flex items-center justify-between text-left hover:bg-gradient-to-r hover:from-sky-50/50 hover:to-orange-50/30 transition-all duration-200"
        >
          <span className="font-medium text-gray-800 pr-4">{question}</span>
          <ChevronDownIcon
            className={`w-5 h-5 text-sky-500 transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
        <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="px-6 pb-5 text-gray-600 leading-relaxed">
            {answer}
          </div>
        </div>
      </div>
    );
  };

  const FAQSection = ({ title, icon: Icon, children, accentColor = 'sky' }) => {
    const colorClasses = {
      sky: 'from-sky-400 to-cyan-500',
      orange: 'from-orange-400 to-amber-500',
      blue: 'from-sky-500 to-blue-500',
      amber: 'from-amber-400 to-orange-500',
      teal: 'from-teal-400 to-cyan-500',
      rose: 'from-rose-400 to-orange-400',
      emerald: 'from-emerald-400 to-teal-500',
    };
    
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[accentColor]} flex items-center justify-center shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
        </div>
        <div>
          {children}
        </div>
      </div>
    );
  };

  const parentsFAQ = (
    <div className="space-y-6">
      {/* Getting Started */}
      <FAQSection title="Getting Started" icon={RocketLaunchIcon} accentColor="sky">
        <FAQItem
          id="p-create-account"
          question="How do I create an account?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Go to the Handpicked Nanny website</li>
              <li>Click "Sign Up" or "Get Started"</li>
              <li>Choose "I'm a Parent" when prompted</li>
              <li>Enter your email address and create a password</li>
              <li>Verify your email address (check your inbox for a verification link)</li>
              <li>Complete your profile</li>
            </ol>
          }
        />
        <FAQItem
          id="p-what-info"
          question="What information do I need to provide?"
          answer={
            <ul className="list-disc list-inside space-y-2 ml-1">
              <li>Your name and email address</li>
              <li>Your address and location</li>
              <li>Information about your children (names, ages, dates of birth)</li>
              <li>A payment method (credit or debit card)</li>
            </ul>
          }
        />
      </FAQSection>

      {/* Setting Up Your Account */}
      <FAQSection title="Setting Up Your Account" icon={Cog6ToothIcon} accentColor="orange">
        <FAQItem
          id="p-setup-profile"
          question="How do I set up my profile?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Log in to your account</li>
              <li>Click on "Profile" in the navigation menu</li>
              <li>Click "Edit Profile"</li>
              <li>Fill in all relevant information: Add your children's information, set your location, add any special requirements</li>
              <li>Save your changes</li>
            </ol>
          }
        />
        <FAQItem
          id="p-add-payment"
          question="How do I add a payment method?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Go to "Profile" → "Edit Profile"</li>
              <li>Scroll to the "Payment Information" section</li>
              <li>Enter your card details: Card number, expiration date, CVV (security code)</li>
              <li>Click "Save Card"</li>
              <li>Your card will be securely stored for future payments</li>
            </ol>
          }
        />
        <FAQItem
          id="p-payment-secure"
          question="Is my payment information secure?"
          answer="Yes! We use Stripe, a PCI-compliant payment processor trusted by millions of businesses worldwide. Your card information is encrypted and never stored on our servers."
        />
      </FAQSection>

      {/* Posting a Job */}
      <FAQSection title="Posting a Job" icon={DocumentTextIcon} accentColor="sky">
        <FAQItem
          id="p-post-job"
          question="How do I post a job?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Click "Post A Job" in the navigation menu</li>
              <li>Fill in the job details: Job title, description, number of children, pay rate, schedule, and location</li>
              <li>Review all information</li>
              <li>Click "Post Job"</li>
            </ol>
          }
        />
        <FAQItem
          id="p-job-description"
          question="What should I include in the job description?"
          answer={
            <ul className="list-disc list-inside space-y-2 ml-1">
              <li>What activities you'd like the sitter to do (homework help, meal prep, outdoor play, etc.)</li>
              <li>Any special needs or requirements</li>
              <li>House rules or expectations</li>
              <li>Whether the sitter needs to drive or have their own transportation</li>
            </ul>
          }
        />
        <FAQItem
          id="p-overnight-care"
          question="How do I post overnight care or care extending beyond 11:59 p.m.?"
          answer={
            <div className="space-y-3">
              <p>Please select the two dates that care will be required. For day one, set the end time to <strong>11:59 p.m.</strong>, and for day two, set the start time to <strong>12:00 a.m.</strong></p>
              <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg">
                <p className="font-semibold text-sky-900 mb-1">Example:</p>
                <p className="text-sky-800">If you need care from 7:00 p.m. – 3:00 a.m. on July 5th, your time ranges will be:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-sky-800">
                  <li>July 5th: 7:00 p.m. – 11:59 p.m.</li>
                  <li>July 6th: 12:00 a.m. – 3:00 a.m.</li>
                </ul>
              </div>
              <p>The caregiver will adjust the end time on their work log to reflect the exact time they were relieved of childcare duties. Therefore, if care extends past midnight but the full three hours is not required, the time will be adjusted accordingly.</p>
            </div>
          }
        />
        <FAQItem
          id="p-job-requirements"
          question="Are there any specific job posting requirements?"
          answer={
            <div>
              <p className="mb-3">Yes, there are two minimum requirements:</p>
              <ol className="list-decimal list-inside space-y-2 ml-1">
                <li>A 3-hour minimum time range for all listings</li>
                <li>A $22/hr minimum rate for all jobs</li>
              </ol>
              <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                With the three hour booking minimum in place, you are guaranteeing three hours of pay to the caregiver even if you relieve them of duties prior to the three hour mark. When they submit their work log, it will not allow them to submit less than three hours of work per day.
              </p>
            </div>
          }
        />
        <FAQItem
          id="p-edit-job"
          question="Can I edit a job after posting it?"
          answer={
            <div className="space-y-2">
              <p>You can edit an open job’s details anytime before a sitter has confirmed it.</p>
              <p>Once a sitter has accepted the job, it’s locked and can no longer be edited — this protects the booking you both agreed to. If your plans change:</p>
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li>If you need <strong>fewer hours or fewer days</strong>, just let your sitter know — they log only the hours actually worked, so you’re billed accordingly (subject to the 3-hour daily minimum).</li>
                <li>If you need to <strong>cancel entirely</strong>, use "Cancel Job." A $25 fee applies only if you cancel within 24 hours of the start time.</li>
              </ul>
            </div>
          }
        />
      </FAQSection>

      {/* Reviewing Applications */}
      <FAQSection title="Reviewing Applications" icon={EyeIcon} accentColor="orange">
        <FAQItem
          id="p-see-applications"
          question="How do I see applications for my job?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Go to "Your Jobs" in the navigation menu</li>
              <li>Click on "Listed Jobs" to see all your job postings</li>
              <li>Click on a specific job to see all applications</li>
            </ol>
          }
        />
        <FAQItem
          id="p-applicant-info"
          question="What information can I see about applicants?"
          answer={
            <ul className="list-disc list-inside space-y-2 ml-1">
              <li>Sitter's name and profile picture</li>
              <li>Their bio and experience</li>
              <li>Certifications (CPR, First Aid, etc.)</li>
              <li>Their application message</li>
              <li>Their full profile (click to view details)</li>
            </ul>
          }
        />
        <FAQItem
          id="p-message-before"
          question="Can I message a sitter before accepting?"
          answer="Messaging is only available during active jobs. Once a job is accepted and in progress, you'll be able to communicate with your sitter through the messaging system."
        />
      </FAQSection>

      {/* Making Payments */}
      <FAQSection title="Making Payments" icon={CreditCardIcon} accentColor="sky">
        <FAQItem
          id="p-when-pay"
          question="When do I need to pay?"
          answer={
            <div>
              <p className="mb-3">Payment is required after the job is completed:</p>
              <ol className="list-decimal list-inside space-y-2 ml-1">
                <li>The sitter submits a work log with hours worked</li>
                <li>You receive a notification to review and approve</li>
                <li>You can review the hours and approve payment</li>
                <li>Payment is processed immediately upon approval</li>
              </ol>
              <p className="mt-3 text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">If you don't review within 24 hours, payment is automatically processed.</p>
            </div>
          }
        />
        <FAQItem
          id="p-how-much"
          question="How much will I be charged?"
          answer={
            <div>
              <p className="mb-3">You pay:</p>
              <ul className="list-disc list-inside space-y-2 ml-1">
                <li><strong>Original Job Amount:</strong> The agreed-upon hourly rate × hours worked</li>
                <li><strong>Platform Fee:</strong> 10% of the original job amount</li>
                <li><strong>Processing Fee:</strong> covers payment processing (roughly 3.5% of the job amount + $0.57)</li>
                <li><strong>Tip (optional):</strong> you can add a tip at checkout — 100% of it goes to your sitter</li>
              </ul>
              <div className="mt-4 p-4 bg-gradient-to-r from-sky-50 to-cyan-50 rounded-xl border border-sky-200">
                <p className="font-semibold text-sky-900">Example: For a $100 job (5 hours × $20/hour):</p>
                <ul className="text-sm mt-3 space-y-1 text-sky-800">
                  <li>Original amount: $100.00</li>
                  <li>Platform fee (10%): $10.00</li>
                  <li>Processing fee: $4.11</li>
                  <li className="font-bold pt-2 border-t border-sky-200 mt-2">Total you pay: $114.11</li>
                </ul>
                <p className="text-sm mt-3 text-sky-600 font-medium">The sitter receives the full $100.00.</p>
              </div>
            </div>
          }
        />
        <FAQItem
          id="p-receipt"
          question="Will I receive a receipt?"
          answer="Yes! You'll receive an email receipt from Stripe with all payment details, including amount paid, fees, payment date, and receipt number."
        />
      </FAQSection>

      {/* Cancellations */}
      <FAQSection title="Cancellations" icon={XCircleIcon} accentColor="orange">
        <FAQItem
          id="p-cancel-job"
          question="Can I cancel a job?"
          answer={
            <div>
              <p className="mb-3">Yes, but cancellation policies apply:</p>
              <ul className="space-y-2 ml-1">
                <li className="flex items-start gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
                  <span><strong>More than 24 hours before job start:</strong> No fee</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircleIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Within 24 hours of job start:</strong> $25 cancellation fee</span>
                </li>
              </ul>
            </div>
          }
        />
        <FAQItem
          id="p-how-cancel"
          question="How do I cancel a job?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Go to "Your Jobs"</li>
              <li>Find the job you want to cancel</li>
              <li>Click "Cancel Job"</li>
              <li>Confirm the cancellation</li>
            </ol>
          }
        />
      </FAQSection>

      {/* Reviews */}
      <FAQSection title="Reviews" icon={StarIcon} accentColor="sky">
        <FAQItem
          id="p-leave-review"
          question="Can I leave a review for a sitter?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Go to "Your Jobs" → "Completed Jobs"</li>
              <li>Find the completed job</li>
              <li>Click "Leave Review"</li>
              <li>Rate the sitter (1-5 stars)</li>
              <li>Write a comment about your experience</li>
              <li>Submit your review</li>
            </ol>
          }
        />
        <FAQItem
          id="p-why-reviews"
          question="Why are reviews important?"
          answer={
            <ul className="list-disc list-inside space-y-2 ml-1">
              <li>Build trust in the community</li>
              <li>Help other parents find quality sitters</li>
              <li>Help sitters find good families to work with</li>
              <li>Improve the overall experience for everyone</li>
            </ul>
          }
        />
      </FAQSection>
    </div>
  );

  const sittersFAQ = (
    <div className="space-y-6">
      {/* Getting Started */}
      <FAQSection title="Getting Started" icon={RocketLaunchIcon} accentColor="orange">
        <FAQItem
          id="s-create-account"
          question="How do I create an account?"
          answer={
            <div className="space-y-3">
              <p className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-900">
                Sitter accounts are by invitation. Your email must be approved by our team before you can sign up — email <a href="mailto:childcare@handpickednanny.com" className="underline font-medium">childcare@handpickednanny.com</a> to get started.
              </p>
              <ol className="list-decimal list-inside space-y-2 ml-1">
                <li>Once approved, go to the Handpicked Nanny website</li>
                <li>Click "Sign Up" or "Get Started"</li>
                <li>Choose "I'm a Sitter" when prompted</li>
                <li>Enter your (approved) email address and create a password</li>
                <li>Verify your email address (check your inbox for a verification link)</li>
                <li>Complete your profile</li>
              </ol>
            </div>
          }
        />
        <FAQItem
          id="s-what-info"
          question="What information do I need to provide?"
          answer={
            <ul className="list-disc list-inside space-y-2 ml-1">
              <li>Your name and email address</li>
              <li>A profile picture</li>
              <li>Your bio and experience</li>
              <li>Your address and location</li>
              <li>Any certifications you have (CPR, First Aid, etc.)</li>
            </ul>
          }
        />
      </FAQSection>

      {/* Setting Up Your Account */}
      <FAQSection title="Setting Up Your Account" icon={Cog6ToothIcon} accentColor="sky">
        <FAQItem
          id="s-setup-profile"
          question="How do I set up my profile?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Log in to your account</li>
              <li>Click on "Profile" in the navigation menu</li>
              <li>Click "Edit Profile"</li>
              <li>Upload a clear profile picture</li>
              <li>Write a compelling bio highlighting your experience</li>
              <li>Add your certifications</li>
              <li>Save your changes</li>
            </ol>
          }
        />
        <FAQItem
          id="s-why-stripe"
          question="Why do I need to connect a Stripe account?"
          answer="You need to connect a Stripe account to receive payments from parents. Stripe is a secure payment processor that handles all transactions. Without a connected Stripe account, you won't be able to receive payments for completed jobs."
        />
        <FAQItem
          id="s-connect-stripe"
          question="How do I connect my Stripe account?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Go to "Profile" → "Edit Profile"</li>
              <li>Scroll to the "Payment Setup" section</li>
              <li>Click "Connect Stripe Account" or "Complete Stripe Onboarding"</li>
              <li>You'll be redirected to Stripe's secure onboarding page</li>
              <li>Enter your personal information (name, date of birth, address)</li>
              <li>Add your bank account details for payouts</li>
              <li>Verify your identity if requested</li>
              <li>Once complete, you'll be redirected back to your profile</li>
            </ol>
          }
        />
        <FAQItem
          id="s-stripe-time"
          question="How long does Stripe onboarding take?"
          answer="The initial setup usually takes 5-10 minutes. However, Stripe may need to verify your identity, which can take 1-2 business days. You'll receive email updates from Stripe about your account status."
        />
        <FAQItem
          id="s-stripe-onboarding-steps"
          question="How do I fill out the Stripe Express onboarding form?"
          answer={
            <div>
              <ol className="list-decimal list-inside space-y-3 ml-1">
                <li>Select <strong>Individual</strong> for business type.</li>
                <li>Fill out your personal details: name, email, date of birth, address, phone number, and last 4 digits of your Social Security number.</li>
                <li>
                  For business details, set the industry to <strong>Education → Child Care</strong>.
                  <ol className="list-decimal list-inside space-y-1 ml-4 mt-1">
                    <li>Click <strong>"Don't have a website"</strong> and add a brief description of the child care services you provide.</li>
                  </ol>
                </li>
                <li>Connect your bank account to receive payouts.</li>
              </ol>
              <p className="mt-4 text-sm text-gray-500">
                After setup, you can manage your Stripe account at{' '}
                <a href="https://connect.stripe.com/express_login" target="_blank" rel="noopener noreferrer" className="text-sky-600 underline hover:text-sky-800">
                  connect.stripe.com/express_login
                </a>.
              </p>
            </div>
          }
        />
      </FAQSection>

      {/* Finding and Applying for Jobs */}
      <FAQSection title="Finding Jobs" icon={MagnifyingGlassIcon} accentColor="orange">
        <FAQItem
          id="s-find-jobs"
          question="How do I find available jobs?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Log in to your account</li>
              <li>Click "Jobs List" in the navigation menu</li>
              <li>Browse available job postings</li>
              <li>Use filters to find jobs that match your preferences (location, pay rate, schedule, etc.)</li>
            </ol>
          }
        />
        <FAQItem
          id="s-apply-job"
          question="How do I apply for a job?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Find a job you're interested in</li>
              <li>Click "Apply" on the job listing</li>
              <li>Write a personalized message to the parent explaining why you're a good fit</li>
              <li>Click "Submit Application"</li>
              <li>You'll receive a confirmation that your application was submitted</li>
            </ol>
          }
        />
        <FAQItem
          id="s-multiple-jobs"
          question="Can I apply for multiple jobs?"
          answer="Yes! You can apply for as many jobs as you want. Each application is independent."
        />
      </FAQSection>

      {/* Accepting Job Offers */}
      <FAQSection title="Accepting Job Offers" icon={CheckCircleIcon} accentColor="sky">
        <FAQItem
          id="s-after-accept"
          question="What happens after a parent accepts my application?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>You'll receive a notification</li>
              <li>Go to "Your Jobs" → "Job Offers" to see the offer</li>
              <li>Review the job details carefully</li>
              <li>Click "Accept Offer" to confirm the job</li>
              <li>The job status will change to "Active"</li>
            </ol>
          }
        />
        <FAQItem
          id="s-decline-offer"
          question="Can I decline a job offer?"
          answer="Yes, you can decline a job offer if it doesn't work for you. However, once you accept an offer, you're committed to completing the job."
        />
      </FAQSection>

      {/* Getting Paid */}
      <FAQSection title="Getting Paid" icon={BanknotesIcon} accentColor="orange">
        <FAQItem
          id="s-log-hours"
          question="How do I log my work hours?"
          answer={
            <div className="space-y-3">
              <ol className="list-decimal list-inside space-y-2 ml-1">
                <li>Go to "Your Jobs" → "Active Jobs"</li>
                <li>Find the completed job</li>
                <li>Click "Submit Work Log"</li>
                <li>Your work log will pre-populate with the hours you were originally booked for. Adjust the start and end times to reflect your actual arrival and departure from the family's home.</li>
                <li>Click "Submit"</li>
              </ol>
              <p className="p-3 bg-sky-50 border border-sky-200 rounded-lg text-sky-900">
                The family is required to book a minimum of three hours for service. As a result, you are guaranteed three hours of pay even if you are relieved of duties prior to the three hour mark. The system will not allow you to submit less than three hours per day for your work log.
              </p>
              <p>For overnight care or any care extending beyond midnight, ensure the time ranges are submitted as follows — the first day ending at <strong>11:59 p.m.</strong> and the second day starting at <strong>12:00 a.m.</strong></p>
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="font-semibold text-orange-900 mb-1">Example:</p>
                <p className="text-orange-800">If you provided care from 7:00 p.m. – 1:30 a.m. on July 5th, submit your time ranges as:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-orange-800">
                  <li>July 5th: 7:00 p.m. – 11:59 p.m.</li>
                  <li>July 6th: 12:00 a.m. – 1:30 a.m.</li>
                </ul>
              </div>
            </div>
          }
        />
        <FAQItem
          id="s-edit-work-log"
          question="Can I edit my work log after submitting it?"
          answer={
            <div className="space-y-3">
              <p>Yes — as long as the parent hasn't paid yet, you can adjust your submitted hours.</p>
              <ol className="list-decimal list-inside space-y-2 ml-1">
                <li>Go to "Your Jobs" → "Pending Hours" tab</li>
                <li>Find the job you want to update</li>
                <li>Click the orange <strong>"Edit Work Log"</strong> button</li>
                <li>Adjust the times as needed</li>
                <li>Click "Save Changes"</li>
              </ol>
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Once the parent approves and pays (or 24 hours passes and auto-pay triggers), the log is locked and the Edit button will no longer appear.
              </p>
            </div>
          }
        />
        <FAQItem
          id="s-when-paid"
          question="When will I get paid?"
          answer={
            <div>
              <ul className="space-y-3 ml-1">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sky-600 text-sm font-bold">1</span>
                  </div>
                  <span><strong>Manual Payment:</strong> The parent will receive a notification to review and pay your work log. Payment is processed once they approve it.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-orange-600 text-sm font-bold">2</span>
                  </div>
                  <span><strong>Automatic Payment:</strong> If the parent doesn't review your work log within 24 hours, payment is automatically processed.</span>
                </li>
              </ul>
            </div>
          }
        />
        <FAQItem
          id="s-how-much"
          question="How much will I receive?"
          answer={
            <div>
              <p>You receive the full amount agreed upon in the job posting. For example, if a job pays $20/hour and you worked 5 hours, you'll receive $100.</p>
              <div className="mt-3 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200">
                <p className="text-orange-800 font-semibold flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5" />
                  Parents pay all fees (platform fee and processing fees), so you receive 100% of the agreed-upon amount.
                </p>
              </div>
            </div>
          }
        />
        <FAQItem
          id="s-receive-payment"
          question="How do I receive my payment?"
          answer="Payments are sent directly to your connected Stripe account. From there, Stripe transfers the funds to your bank account according to your Stripe payout schedule (typically 2-7 business days)."
        />
        <FAQItem
          id="s-view-payouts"
          question="How do I view my payouts?"
          answer={
            <div>
              <p className="mb-3">You can view your payouts through the Stripe Express dashboard:</p>
              <ol className="list-decimal list-inside space-y-2 ml-1">
                <li>Visit <a href="https://connect.stripe.com/express_login" target="_blank" rel="noopener noreferrer" className="text-sky-600 underline hover:text-sky-800">connect.stripe.com/express_login</a></li>
                <li>Enter the email address you used during Stripe setup.</li>
                <li>Stripe will send a login code to the phone number you provided during onboarding.</li>
                <li>Enter the code to access your dashboard and view your payout history.</li>
              </ol>
            </div>
          }
        />
      </FAQSection>

      {/* Cancellations */}
      <FAQSection title="Cancellations" icon={XCircleIcon} accentColor="sky">
        <FAQItem
          id="s-cancel-job"
          question="Can I cancel a job?"
          answer={
            <div>
              <p className="mb-3">Yes, but cancellation policies apply:</p>
              <ul className="space-y-2 ml-1">
                <li className="flex items-start gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
                  <span><strong>More than 24 hours before job start:</strong> No fee</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircleIcon className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Within 24 hours of job start:</strong> $25 cancellation fee</span>
                </li>
              </ul>
            </div>
          }
        />
        <FAQItem
          id="s-how-cancel"
          question="How do I cancel a job?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Go to "Your Jobs"</li>
              <li>Find the job you want to cancel</li>
              <li>Click "Cancel Job"</li>
              <li>Confirm the cancellation</li>
            </ol>
          }
        />
      </FAQSection>

      {/* Stripe Connect */}
      <FAQSection title="Stripe Connect & Payments" icon={BuildingLibraryIcon} accentColor="orange">
        <FAQItem
          id="s-what-stripe"
          question="What is Stripe Connect?"
          answer="Stripe Connect is Stripe's platform that allows you to receive payments directly. When you connect your Stripe account, payments go directly to you (minus platform fees, which are paid by parents)."
        />
        <FAQItem
          id="s-stripe-info"
          question="What information does Stripe need?"
          answer={
            <ul className="list-disc list-inside space-y-2 ml-1">
              <li>Your full legal name</li>
              <li>Date of birth</li>
              <li>Social Security Number (SSN) or Tax ID</li>
              <li>Home address</li>
              <li>Bank account details for payouts</li>
              <li>Government-issued ID (for verification)</li>
            </ul>
          }
        />
        <FAQItem
          id="s-payout-time"
          question="How long does it take to receive payouts?"
          answer={
            <div>
              <p className="mb-3">Once a payment is processed:</p>
              <ul className="list-disc list-inside space-y-2 ml-1">
                <li>Stripe typically transfers funds to your bank account within 2-7 business days</li>
                <li>Your first payout may take longer for verification purposes</li>
                <li>You can check your payout schedule in your Stripe Dashboard</li>
              </ul>
            </div>
          }
        />
        <FAQItem
          id="s-fees"
          question="Are there any fees for receiving payments?"
          answer="No! You receive 100% of the agreed-upon job amount. All platform fees and processing fees are paid by the parent."
        />
      </FAQSection>

      {/* Reviews */}
      <FAQSection title="Reviews" icon={StarIcon} accentColor="sky">
        <FAQItem
          id="s-leave-review"
          question="Can I leave a review for a parent?"
          answer={
            <ol className="list-decimal list-inside space-y-2 ml-1">
              <li>Go to "Your Jobs" → "Completed Jobs"</li>
              <li>Find the completed job</li>
              <li>Click "Leave Review"</li>
              <li>Rate the parent (1-5 stars)</li>
              <li>Write a comment about your experience</li>
              <li>Submit your review</li>
            </ol>
          }
        />
        <FAQItem
          id="s-see-reviews"
          question="Can I see reviews parents leave about me?"
          answer="Yes! Reviews from parents appear on your profile and help build your reputation."
        />
      </FAQSection>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50/50 via-white to-orange-50/30 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-400 to-cyan-500 shadow-lg shadow-sky-400/30 mb-6">
            <QuestionMarkCircleIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-sky-500 via-cyan-500 to-sky-600 bg-clip-text text-transparent mb-4">
            Help Center
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Find answers to common questions about using Handpicked Nanny
          </p>
        </div>

        {/* Interactive Tutorial Banner */}
        <div className="mb-10 bg-gradient-to-r from-sky-400 to-cyan-500 rounded-2xl p-6 sm:p-7 shadow-xl shadow-sky-400/25 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-center sm:text-left">
            <div className="hidden sm:flex w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm items-center justify-center flex-shrink-0">
              <SparklesIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">New here? Take the interactive tour</h2>
              <p className="text-sky-100 text-sm mt-0.5">A hands-on walkthrough of the whole process — from signup to getting paid.</p>
            </div>
          </div>
          <button
            onClick={() => setShowTutorial(true)}
            style={{ touchAction: 'manipulation' }}
            className="px-6 py-3 bg-white text-sky-600 font-bold rounded-xl hover:bg-sky-50 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 whitespace-nowrap"
          >
            Start the Tour
          </button>
        </div>

        {showTutorial && <Tutorial onClose={() => setShowTutorial(false)} initialRole={tourParam} />}

        {/* Tab Navigation */}
        <div className="flex justify-center mb-10">
          <div className="bg-white rounded-2xl p-2 shadow-lg shadow-gray-200/50 border border-gray-100 inline-flex">
            <button
              onClick={() => setActiveTab('parents')}
              className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center gap-3 ${
                activeTab === 'parents'
                  ? 'bg-gradient-to-r from-sky-400 to-cyan-500 text-white shadow-lg shadow-sky-400/30'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <UserGroupIcon className="w-5 h-5" />
              For Parents
            </button>
            <button
              onClick={() => setActiveTab('sitters')}
              className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center gap-3 ${
                activeTab === 'sitters'
                  ? 'bg-gradient-to-r from-orange-400 to-amber-500 text-white shadow-lg shadow-orange-400/30'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <UserIcon className="w-5 h-5" />
              For Sitters
            </button>
          </div>
        </div>

        {/* FAQ Content */}
        <div className="transition-all duration-300">
          {activeTab === 'parents' ? parentsFAQ : sittersFAQ}
        </div>

        {/* Contact Section */}
        <div className="mt-16 bg-gradient-to-r from-sky-400 via-cyan-500 to-sky-500 rounded-3xl p-10 text-white text-center shadow-2xl shadow-sky-400/30 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-60 h-60 bg-orange-300 rounded-full translate-x-1/3 translate-y-1/3"></div>
          </div>
          
          <div className="relative">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm mb-6">
              <EnvelopeIcon className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-3">Still need help?</h2>
            <p className="text-sky-100 mb-6 text-lg">Can't find what you're looking for? Email us:</p>
            <a
              href="mailto:childcare@handpickednanny.com"
              className="inline-block text-xl sm:text-2xl font-bold underline decoration-2 underline-offset-4 hover:text-sky-100 transition-colors duration-200 break-all"
            >
              childcare@handpickednanny.com
            </a>
            <div className="mt-6">
              <button
                onClick={() => {
                  navigator.clipboard.writeText('childcare@handpickednanny.com');
                  const btn = document.getElementById('copy-email-btn');
                  if (btn) {
                    const original = btn.textContent;
                    btn.textContent = 'Copied!';
                    setTimeout(() => { btn.textContent = original; }, 2000);
                  }
                }}
                id="copy-email-btn"
                className="inline-flex items-center px-6 py-3 bg-white text-sky-600 font-bold rounded-xl hover:bg-sky-50 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                <EnvelopeIcon className="w-5 h-5 mr-3" />
                Copy Email
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
